import { Dispatch, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Autocomplete, Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { IWishList } from '../../types/worksheet';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar } from '../../features/snackbar/snackbarSlice';

const MAX_CHOICES = 5;

interface IAvailableUnit {
  modelType: string;
  unitType: string;
  level: string | null;
  exposure: string;
  size: number;
}

export type WishlistAction =
  | { type: 'ADD_CHOICE' }
  | { type: 'REMOVE_CHOICE'; index: number }
  | { type: 'SET_UNIT_TYPE'; index: number; value: string }
  | { type: 'SET_MODEL_TYPE'; index: number; value: string[] }
  | { type: 'SET_EXPOSURE'; index: number; value: string }
  | { type: 'SET_LEVEL'; index: number; value: string };

export const initialWishlistChoice: IWishList = { unitType: '', modelTypes: ['Any'], exposure: 'Any', level: 'Any' };

export const wishlistChoicesReducer = (state: IWishList[], action: WishlistAction): IWishList[] => {
  switch (action.type) {
    case 'ADD_CHOICE':
      return [...state, { ...initialWishlistChoice }];
    case 'REMOVE_CHOICE':
      return state.filter((choice, index) => index !== action.index);
    case 'SET_UNIT_TYPE':
      // Changing the unit type starts the choice over, since model/view/level depend on it
      return state.map((choice, index) => (index === action.index ? { ...initialWishlistChoice, unitType: action.value } : choice));
    case 'SET_MODEL_TYPE':
      return state.map((choice, index) => (index === action.index ? { ...choice, modelTypes: action.value } : choice));
    case 'SET_EXPOSURE':
      return state.map((choice, index) => (index === action.index ? { ...choice, exposure: action.value } : choice));
    case 'SET_LEVEL':
      return state.map((choice, index) => (index === action.index ? { ...choice, level: action.value } : choice));
    default:
      return state;
  }
};

export const isWishlistChoiceFilled = ({ unitType, modelTypes }: IWishList): boolean => !!unitType && !!modelTypes?.length;

export const isWishlistFilled = (choices: IWishList[]): boolean => choices.every(isWishlistChoiceFilled);

const sortUnitTypes = (a: string, b: string) => {
  if (a === 'Studio') return -1;
  if (b === 'Studio') return 1;
  return a.localeCompare(b);
};

// Splits `number` into `parts` whole numbers that add back up to `number`, larger parts first
const splitNumber = (number: number, parts: number) => {
  const base = Math.floor(number / parts);
  const split = Array(parts).fill(base);
  for (let i = 0; i < number - base * parts; i++) split[i]++;
  return split;
};

const WishlistChoices = (props: ChildProps) => {
  const { choices, dispatch, projectId, showUnitTypes, showErrors, setShowErrors } = props;
  const storeDispatch = useAppDispatch();

  const { data } = useQuery<{ getAvailableUnitTypes: IAvailableUnit[] }>(AVAILABLEUNITTYPES, {
    skip: !projectId,
    variables: { projectId },
    onError: (err) => {
      console.log(err);
      storeDispatch(showErrorSnackbar('Unable to load available units.'));
    },
  });
  const allUnits = useMemo(() => data?.getAvailableUnitTypes ?? [], [data]);

  const unitTypes = useMemo(() => Array.from(new Set(allUnits.map((unit) => unit.unitType))).sort(sortUnitTypes), [allUnits]);

  const modelTypes = useMemo(() => {
    const unique = new Map<string, IAvailableUnit>();
    allUnits.forEach((unit) => {
      if (!unique.has(unit.modelType)) unique.set(unit.modelType, unit);
    });
    return Array.from(unique.values()).sort((a, b) => a.modelType.localeCompare(b.modelType));
  }, [allUnits]);

  // Levels are split into low / middle / high thirds
  const { levels, splitLevels } = useMemo(() => {
    const levelNumbers = allUnits.map((unit) => parseInt(unit.level ?? '')).filter((level) => !isNaN(level));
    const min = levelNumbers.length ? Math.min(...levelNumbers) : 0;
    const max = levelNumbers.length ? Math.max(...levelNumbers) : 0;
    return { levels: { min, max }, splitLevels: splitNumber(max, 3) };
  }, [allUnits]);

  const handleAddChoice = () => {
    if (!isWishlistChoiceFilled(choices[choices.length - 1])) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    dispatch({ type: 'ADD_CHOICE' });
  };

  const canRemove = choices.length > 1;

  const renderChoice = (choice: IWishList, index: number) => {
    // Narrow the available units down by whatever has been picked so far
    let filteredUnits = allUnits;
    if (choice.unitType) {
      filteredUnits = filteredUnits.filter((unit) => unit.unitType === choice.unitType);
    }
    if (!choice.modelTypes.includes('Any') || choice.modelTypes.length === 0) {
      filteredUnits = filteredUnits.filter((unit) => choice.modelTypes.includes(unit.modelType));
    }
    const exposures = Array.from(new Set(filteredUnits.map((unit) => unit.exposure))).sort();
    if (choice.exposure !== 'Any' && choice.exposure !== null) {
      filteredUnits = filteredUnits.filter((unit) => unit.exposure === choice.exposure);
    }

    // Disable a level range when none of the remaining units sit in it
    let disableLow = true;
    let disableMid = true;
    let disableHigh = true;
    filteredUnits.forEach((unit) => {
      const level = parseInt(unit.level ?? '');
      if (isNaN(level)) return;
      if (level <= splitLevels[0]) disableLow = false;
      else if (level <= splitLevels[0] + splitLevels[1]) disableMid = false;
      else if (level <= splitLevels[0] + splitLevels[1] + splitLevels[2]) disableHigh = false;
    });

    const unitTypeMissing = showErrors && !choice.unitType;
    // Picking a model is what sets the unit type in this layout, so no unit type means no model yet
    const modelTypeMissing = showErrors && !choice.unitType;
    const selectedModelType = choice.modelTypes[0] && choice.modelTypes[0] !== 'Any' ? choice.modelTypes[0] : '';

    const handleModelTypeChange = (event: any, value: IAvailableUnit | null) => {
      dispatch({ type: 'SET_UNIT_TYPE', index, value: value ? value.unitType : '' });
      dispatch({ type: 'SET_MODEL_TYPE', index, value: value ? [value.modelType] : [] });
    };

    return (
      <Box key={index} sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, mb: 1 }}>Choice #{index + 1}</Typography>
        <Grid container spacing={2}>
          {showUnitTypes ? (
            <Grid item xs={12} md>
              <Tooltip title="Choose the number of bedrooms" placement="top">
                <Autocomplete
                  size="small"
                  options={unitTypes}
                  value={choice.unitType || null}
                  onChange={(event, value) => dispatch({ type: 'SET_UNIT_TYPE', index, value: value ?? '' })}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Unit Type"
                      placeholder="Unit Type"
                      error={unitTypeMissing}
                      helperText={unitTypeMissing ? 'Missing Unit Type' : ''}
                    />
                  )}
                />
              </Tooltip>
            </Grid>
          ) : (
            <Grid item xs={12} md>
              <Tooltip title="Choose a model" placement="top">
                <Autocomplete
                  size="small"
                  options={modelTypes}
                  value={modelTypes.find((option) => option.modelType === selectedModelType) ?? null}
                  onChange={handleModelTypeChange}
                  getOptionLabel={(option) => `${option.modelType} (${option.unitType})`}
                  isOptionEqualToValue={(option, value) => option.modelType === value.modelType}
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Model Type"
                      placeholder="Model Type"
                      error={modelTypeMissing}
                      helperText={modelTypeMissing ? 'Missing Model Type' : ''}
                    />
                  )}
                />
              </Tooltip>
            </Grid>
          )}
          <Grid item xs={12} md>
            <Tooltip title="Choose the exposure of the unit" placement="top">
              <Autocomplete
                size="small"
                options={['Any', ...exposures]}
                value={choice.exposure}
                onChange={(event, value) => dispatch({ type: 'SET_EXPOSURE', index, value: value ?? 'Any' })}
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label="View Type" placeholder="View Type" />}
              />
            </Tooltip>
          </Grid>
          <Grid item xs={12} md>
            <Tooltip title="Choose a preferred level" placement="top">
              <FormControl size="small" sx={{ width: '100%' }}>
                <InputLabel id={`wishlist-level-${index}`}>Level</InputLabel>
                <Select
                  labelId={`wishlist-level-${index}`}
                  value={choice.level}
                  label="Level"
                  onChange={(event) => dispatch({ type: 'SET_LEVEL', index, value: event.target.value })}
                >
                  <MenuItem value="Any">Any</MenuItem>
                  <MenuItem disabled={disableLow} value={`Low ${levels.min}-${splitLevels[0]}`}>
                    Low ({levels.min}-{splitLevels[0]})
                  </MenuItem>
                  <MenuItem disabled={disableMid} value={`Middle ${splitLevels[0] + 1}-${splitLevels[0] + splitLevels[1]}`}>
                    Middle ({splitLevels[0] + 1}-{splitLevels[0] + splitLevels[1]})
                  </MenuItem>
                  <MenuItem disabled={disableHigh} value={`High ${splitLevels[0] + splitLevels[1] + 1}-${levels.max}`}>
                    High ({splitLevels[0] + splitLevels[1] + 1}-{levels.max})
                  </MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
          </Grid>
          {showUnitTypes ? (
            <Grid item xs={12} md>
              <Tooltip title="Enter a model type" placement="top">
                <TextField
                  size="small"
                  fullWidth
                  label="Model Type"
                  placeholder="Model Type"
                  value={selectedModelType}
                  // Blank text means no model preference, the same as "Any" - not a model literally named ""
                  onChange={(event) =>
                    dispatch({ type: 'SET_MODEL_TYPE', index, value: [event.target.value.trim() ? event.target.value : 'Any'] })
                  }
                />
              </Tooltip>
            </Grid>
          ) : null}
          {canRemove ? (
            <Grid item xs={12} md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
              <Button variant="outlined" color="error" onClick={() => dispatch({ type: 'REMOVE_CHOICE', index })}>
                Remove
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      {choices.map(renderChoice)}
      {choices.length < MAX_CHOICES ? (
        <Button variant="contained" onClick={handleAddChoice}>
          + Add Another Choice
        </Button>
      ) : null}
    </Box>
  );
};

interface ChildProps {
  choices: IWishList[];
  dispatch: Dispatch<WishlistAction>;
  projectId: string;
  showUnitTypes: boolean;
  showErrors: boolean;
  setShowErrors: (showErrors: boolean) => void;
}

export const AVAILABLEUNITTYPES = gql`
  query getAvailableUnitTypes($projectId: MongoID!) {
    getAvailableUnitTypes(projectId: $projectId) {
      modelType
      unitType
      level
      exposure
      size
    }
  }
`;

export default WishlistChoices;
